import { create } from 'zustand';

interface BLEDeviceState {
  device: BluetoothDevice | null;
  isConnecting: boolean;
  isConnected: boolean;
  error: string | null;
  monitoredDevices: Map<string, { patientId?: string, organizationId?: string }>;
  connect: (device: BluetoothDevice) => Promise<void>;
  disconnect: () => Promise<void>;
  reset: () => void;
  startMonitoring: (device: BluetoothDevice, organizationId: string, patientId?: string) => void;
  stopMonitoring: (deviceId: string) => void;
  isDeviceMonitored: (deviceId: string) => boolean;
  getMonitoringInfo: (deviceId: string) => { patientId?: string, organizationId?: string } | undefined;
}

export const useBleDeviceStore = create<BLEDeviceState>((set, get) => ({
  device: null,
  isConnecting: false,
  isConnected: false,
  error: null,
  monitoredDevices: new Map(),

  connect: async (device: BluetoothDevice) => {
    try {
      set({ isConnecting: true, error: null });
      const server = await device.gatt?.connect();
      if (!server) throw new Error('Failed to connect to device');
      
      set({ device, isConnected: true });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ isConnecting: false });
    }
  },

  disconnect: async () => {
    const { device } = get();
    if (device?.gatt?.connected) {
      // await device.gatt.disconnect();
    }
    set({ device: null, isConnected: false });
  },

  reset: () => {
    set({ device: null, isConnecting: false, isConnected: false, error: null });
  },

  startMonitoring: (device: BluetoothDevice, organizationId: string, patientId?: string) => {
    const { monitoredDevices } = get();
    const newMonitoredDevices = new Map(monitoredDevices);
    newMonitoredDevices.set(device.id, { organizationId, patientId });
    
    // Persist monitoring info in localStorage
    localStorage.setItem(`monitoring-${device.id}`, JSON.stringify({ 
      organizationId, 
      patientId, 
      deviceName: device.name 
    }));
    
    set({ monitoredDevices: newMonitoredDevices });
  },

  stopMonitoring: (deviceId: string) => {
    const { monitoredDevices } = get();
    const newMonitoredDevices = new Map(monitoredDevices);
    newMonitoredDevices.delete(deviceId);
    
    // Remove from localStorage
    localStorage.removeItem(`monitoring-${deviceId}`);
    
    set({ monitoredDevices: newMonitoredDevices });
  },

  isDeviceMonitored: (deviceId: string) => {
    return get().monitoredDevices.has(deviceId);
  },

  getMonitoringInfo: (deviceId: string) => {
    return get().monitoredDevices.get(deviceId);
  }
}));

// Load persisted monitoring information
const loadPersistedMonitoring = () => {
  const monitoredDevices = new Map();
  
  // Find all localStorage keys that start with 'monitoring-'
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('monitoring-')) {
      try {
        const deviceId = key.replace('monitoring-', '');
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        if (data.organizationId) {
          monitoredDevices.set(deviceId, {
            organizationId: data.organizationId,
            patientId: data.patientId
          });
        }
      } catch (error) {
        console.error('Error loading persisted monitoring info:', error);
      }
    }
  }
  
  useBleDeviceStore.setState({ monitoredDevices });
};

// Initialize persisted monitoring data
loadPersistedMonitoring();
